import { useState, useEffect } from 'react';
import { SchemaExtractionData, ExtractedDataField } from '../../../types/dataTypes';
import { getSchemaExtractionData, updateExtractedFieldValue, updateFieldValidationStatus } from '../services/dataService';

interface UseSchemaExtractionDataReturn {
  schemas: SchemaExtractionData[];
  isLoading: boolean;
  error: string | null;
  updateField: (schemaId: string, fieldId: string, newValue: string) => Promise<void>;
  validateField: (schemaId: string, fieldId: string, isValid: boolean | null) => Promise<void>;
  refreshData: () => Promise<void>;
}

export function useSchemaExtractionData(executionId?: string, userId?: string): UseSchemaExtractionDataReturn {
  const [schemas, setSchemas] = useState<SchemaExtractionData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = async (execId: string, uid?: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getSchemaExtractionData(execId, uid);
      setSchemas(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error loading schema extraction data';
      setError(errorMessage);
      console.error('Error loading schema extraction data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (executionId) {
      loadData(executionId, userId);
    }
  }, [executionId, userId]);

  const updateField = async (schemaId: string, fieldId: string, newValue: string): Promise<void> => {
    try {
      const schema = schemas.find(s => s.id === schemaId);
      if (!schema || !schema.documentId) {
        throw new Error('Schema or document not found');
      }

      const field = schema.extractedFields.find(f => f.id === fieldId);
      if (!field) {
        throw new Error('Field not found');
      }

      // Update in the backend
      await updateExtractedFieldValue(schema.documentId, schemaId, field.name, newValue);

      // Update local state
      setSchemas(prevSchemas => 
        prevSchemas.map(s => 
          s.id === schemaId 
            ? {
                ...s,
                extractedFields: s.extractedFields.map(f => 
                  f.id === fieldId ? { ...f, value: newValue } : f
                )
              }
            : s
        )
      );
    } catch (err) {
      console.error('Error updating field:', err);
      throw err;
    }
  };

  const validateField = async (schemaId: string, fieldId: string, isValid: boolean | null): Promise<void> => {
    try {
      const schema = schemas.find(s => s.id === schemaId);
      if (!schema || !schema.documentId) {
        throw new Error('Schema or document not found');
      }

      const field = schema.extractedFields.find(f => f.id === fieldId);
      if (!field) {
        throw new Error('Field not found');
      }

      // Update in the backend
      await updateFieldValidationStatus(schema.documentId, schemaId, field.name, isValid);

      // Update local state
      setSchemas(prevSchemas => 
        prevSchemas.map(s => 
          s.id === schemaId 
            ? {
                ...s,
                extractedFields: s.extractedFields.map(f => 
                  f.id === fieldId ? { ...f, isValidated: isValid } : f
                )
              }
            : s
        )
      );
    } catch (err) {
      console.error('Error validating field:', err);
      throw err;
    }
  };

  const refreshData = async (): Promise<void> => {
    if (executionId) {
      await loadData(executionId, userId);
    }
  };

  return {
    schemas,
    isLoading,
    error,
    updateField,
    validateField,
    refreshData
  };
}








